import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { IntegrationCard } from '../settings/IntegrationCard';
import { getIntegration, type SettingsIntegration } from '../../lib/settingsApi';
import { ApiError } from '../../lib/api';
import type { ConnectionState } from '../../lib/efacturaApi';
import {
  useDisconnectEfactura,
  useEfacturaStatus,
  useStartEfacturaAuthorization,
} from '../../hooks/useEfactura';
import { useI18n } from '../../contexts/I18nContext';
import { useConfirm } from '../ui/ConfirmProvider';

/**
 * Per-firm ANAF e-Factura (SPV) connection, on the company page next to
 * SmartBill. Two halves:
 *  1. the standard IntegrationCard for the typed fields (CIF, environment) —
 *     token fields are platform-managed and hidden by the card;
 *  2. the authorization state with the "Autorizează la ANAF" button. The
 *     button opens a tab synchronously on click (so it is never blocked as a
 *     pop-up), then points it at ANAF once the backend returns the URL. ANAF
 *     redirects back to this page with `?anaf=ok|error`.
 */

const STATE_STYLES: Record<ConnectionState, string> = {
  connected: 'border-green-200 bg-green-50 text-green-800',
  pending: 'border-amber-200 bg-amber-50 text-amber-800',
  needs_reauth: 'border-red-200 bg-red-50 text-red-800',
  no_spv_rights: 'border-red-200 bg-red-50 text-red-800',
  error: 'border-red-200 bg-red-50 text-red-800',
  not_configured: 'border-gray-200 bg-gray-50 text-gray-700',
};

const STATE_LABELS: Record<ConnectionState, string> = {
  connected: 'Conectat',
  pending: 'Așteaptă autorizarea',
  needs_reauth: 'Necesită reautorizare',
  no_spv_rights: 'Fără drepturi în SPV',
  error: 'Eroare',
  not_configured: 'Neconfigurat',
};

export function EfacturaCard({ companyId }: { companyId: string }) {
  const { t } = useI18n();
  const qc = useQueryClient();
  const confirm = useConfirm();
  const [searchParams, setSearchParams] = useSearchParams();
  const [notice, setNotice] = useState<string | null>(null);

  const queryKey = ['settings', 'integration', 'anafEfactura', companyId] as const;
  const integration = useQuery({
    queryKey,
    queryFn: () => getIntegration('anafEfactura', companyId),
  });
  const hasCif = Boolean(integration.data?.values.cif);
  const status = useEfacturaStatus(companyId, hasCif);
  const start = useStartEfacturaAuthorization();
  const disconnect = useDisconnectEfactura();

  // Landing back from ANAF: show the outcome once, then clean the URL and
  // re-run the real check so "pending" turns into "connected" (or not).
  useEffect(() => {
    const flag = searchParams.get('anaf');
    if (!flag) return;
    setNotice(
      flag === 'ok'
        ? 'ANAF a acceptat autorizarea. Se verifică accesul la SPV…'
        : 'Autorizarea la ANAF nu s-a încheiat. Reîncearcă din butonul de mai jos.',
    );
    const next = new URLSearchParams(searchParams);
    next.delete('anaf');
    setSearchParams(next, { replace: true });
    void qc.invalidateQueries({ queryKey: ['efactura'] });
    void qc.invalidateQueries({ queryKey });
  }, [searchParams.get('anaf')]);

  function handleChanged(updated: SettingsIntegration) {
    qc.setQueryData(queryKey, updated);
    void qc.invalidateQueries({ queryKey: ['efactura', 'status', companyId] });
  }

  function handleAuthorize() {
    setNotice(null);
    // Open synchronously on the click so browsers treat it as user-initiated.
    const tab = window.open('', '_blank');
    start.mutate(companyId, {
      onSuccess: ({ authorizeUrl }) => {
        if (tab) tab.location.href = authorizeUrl;
        else window.location.href = authorizeUrl;
        setNotice(
          'S-a deschis pagina ANAF într-un tab nou. Alege certificatul calificat când browserul îl cere; la final ești adus înapoi aici.',
        );
      },
      onError: (err) => {
        tab?.close();
        setNotice(err instanceof ApiError ? err.message : 'Nu s-a putut porni autorizarea.');
      },
    });
  }

  async function handleDisconnect() {
    const ok = await confirm({
      title: 'Deconectezi firma de la SPV?',
      message:
        'Tokenurile ANAF se șterg din aplicație (și se revocă, dacă ANAF permite). Facturile deja aduse rămân. Poți reautoriza oricând.',
      confirmLabel: 'Deconectează',
      danger: true,
    });
    if (!ok) return;
    disconnect.mutate(companyId, {
      onSuccess: () => setNotice('Firma a fost deconectată de la SPV.'),
      onError: (err) => setNotice(err instanceof ApiError ? err.message : 'Deconectarea a eșuat.'),
    });
  }

  const st = status.data;
  const state: ConnectionState = !hasCif ? 'not_configured' : (st?.connection.state ?? 'pending');
  const message = !hasCif
    ? 'Completează și salvează CIF-ul firmei, apoi autorizează.'
    : (st?.connection.message ?? (status.isLoading ? 'Se verifică conexiunea…' : ''));
  const canAuthorize = hasCif && st?.appConfigured !== false;

  return (
    <div className="mt-6">
      <h2 className="text-base font-semibold text-gray-900">
        {t('companies.detail.efacturaHeading')}
      </h2>
      <p className="mt-1 text-sm text-gray-500">{t('companies.detail.efacturaHint')}</p>

      <div className="mt-4 space-y-4">
        {integration.isLoading && <p className="text-sm text-gray-500">{t('companies.loading')}</p>}
        {integration.isError && (
          <p className="text-sm text-red-600">{t('companies.error.generic')}</p>
        )}
        {integration.data && (
          <IntegrationCard
            integration={integration.data}
            onChanged={handleChanged}
            companyId={companyId}
          />
        )}

        <div className={`rounded-md border px-3 py-2 text-sm ${STATE_STYLES[state]}`}>
          <div className="flex flex-wrap items-center justify-between gap-2">
            <div>
              <strong>{STATE_LABELS[state]}.</strong> {message}
              {st?.connection.authorizedBy && state === 'connected' && (
                <span className="block text-xs opacity-80">
                  Autorizat de {st.connection.authorizedBy}
                  {st.connection.tokenExpiresAt &&
                    ` · tokenul expiră la ${new Date(st.connection.tokenExpiresAt).toLocaleDateString('ro-RO')}`}
                </span>
              )}
              {st && !st.appConfigured && (
                <span className="block text-xs">
                  Aplicația OAuth ANAF nu este configurată: completează Client ID și Client Secret
                  în Setări → Integrări.
                </span>
              )}
            </div>
            <div className="flex flex-wrap gap-2">
              <button
                type="button"
                onClick={handleAuthorize}
                disabled={!canAuthorize || start.isPending}
                className="rounded bg-blue-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-50"
              >
                {state === 'connected' ? 'Reautorizează la ANAF' : 'Autorizează la ANAF'}
              </button>
              {(state === 'connected' || state === 'needs_reauth' || state === 'no_spv_rights') && (
                <button
                  type="button"
                  onClick={() => void handleDisconnect()}
                  disabled={disconnect.isPending}
                  className="rounded border border-gray-300 bg-white px-3 py-1.5 text-sm text-gray-700 hover:bg-gray-50 disabled:opacity-50"
                >
                  Deconectează
                </button>
              )}
            </div>
          </div>
        </div>

        {notice && (
          <div className="rounded-md border border-gray-200 bg-gray-50 px-3 py-2 text-sm text-gray-700">
            {notice}
          </div>
        )}
      </div>
    </div>
  );
}
